import axios from 'axios'
import router from '@/router'
import { resetAuthState } from '@/composables/auth/authState.js'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  },
  // Authentication is carried by the Secure, HttpOnly session cookie.
  withCredentials: true
})

// Flag to prevent multiple simultaneous auth redirects
let isHandlingAuthError = false

// Response interceptor - Handle authentication errors
api.interceptors.response.use(
  response => response,
  error => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Prevent multiple simultaneous redirects when many requests fail at once
      if (!isHandlingAuthError) {
        isHandlingAuthError = true
        console.warn('Authentication failed, clearing session and redirecting to login')

        // Clear the in-memory auth state so the router cannot immediately
        // treat the user as logged in after a 401.
        resetAuthState()

        // Only redirect if not already on login page
        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }

        // Reset flag after a short delay to allow future auth errors to be handled
        setTimeout(() => {
          isHandlingAuthError = false
        }, 1000)
      }
    }

    // Handle 403 Forbidden (wrong domain, etc.)
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data)

      // If domain mismatch, force logout
      if (error.response.data?.error?.includes('domain')) {
        resetAuthState()

        if (router.currentRoute.value.path !== '/login') {
          router.push('/login')
        }
      }
    }

    return Promise.reject(error)
  }
)

// Re-export all services from their feature files for backward compatibility
export { employeeService, teamService } from './employeeService.js'
export { projectService, assignmentService } from './projectService.js'
export {
  prospectsLeadsService,
  prospectSourcingStatusService,
  prospectPreferencesService,
  prospectAssignmentService
} from './prospectService.js'
export { workflowService } from './workflowService.js'
export { actionsService } from './actionsService.js'
export { hoursService } from './hoursService.js'
export { syncService } from './syncService.js'
export {
  authorizedUsersService,
  adminService,
  top20AccountsService,
  budgetService,
  servicesService
} from './adminService.js'
export { predictionService } from './predictionService.js'
export { workforceForecastService } from './workforceForecastService.js'

export default api
